import { createContext, useContext, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  Button,
  CalendarIcon,
  CheckIcon,
  CheckboxField,
  Field,
  Modal,
  PencilIcon,
  PlusCircleSolidIcon,
  SearchIcon,
  SelectField,
  SparklesIcon,
  StatusBadge,
  TextareaField,
  UserIcon,
  type StatusTone,
  ChevronDownIcon,
  ConfirmDialog,
  TrashIcon,
} from "@fawkes/ui";

import { formatDate } from "../i18n/format";
import { WizardRunModal } from "./WizardsTile";
import { handelingLabel, type TaakKeuze } from "./settings/TaakKeuzesSection";
import { useTaakUitgevoerd, vraagUitvoering, type Handeling } from "./taakUitvoeren";

/** "Uitvoeren" bij een taak met een handeling; gezet door de tegel, gelezen door de actieknoppen. */
const UitvoerContext = createContext<((taak: TaskDto) => void) | null>(null);

// Taken RUNTIME migration Epic T5: the SPA port of the hybrid taken React app
// (frontend-react/taken — Jmix AnimalTakenTileFragment/TakenReactHost). The old postMessage bridge
// (taken:ready/taken:data/taken:actie) is replaced by the api endpoints:
//   GET   /api/animals/{dierId}/taken   → TakenPayload (KPI + ordered tasks + option lists + magBeheren)
//   POST  /api/taken                    → create manual task
//   PATCH /api/taken/{id}               → update title/description/priority/deadline/team
//   POST  /api/taken/{id}/status        → status lifecycle (afronden/heropenen/annuleren)
// ALL data labels (status/priority/team/source) come from the server (Dutch) — the client stays dumb
// and only translates its own chrome (buttons, placeholders) via the `taken.*` i18n family.
//
// Both hybrid modes are ported: the compact tile (`view=tegel`: KPI mini-cards + top-5 open tasks +
// create) renders on the animal-detail tile board, and the full view (`view=tab`: 5 KPI cards, search,
// source filter, "toon afgerond", create/edit form, per-row lifecycle actions) opens from the tile's
// "Alles bekijken" in a widened Modal — the same expand pattern the Voeding tile uses for its
// "Overige voedingsregels" overview (the Jmix Taken TAB was removed by request; the tile is the home).
//
// Manage actions (create/edit/status) are gated client-side on the payload's `magBeheren` flag; the
// server enforces the same predicate (ROLE_TAKEN_ADMIN / system-full-access) on every write.
// The hybrid's "openInstellingen" gear is NOT ported: the SPA settings Taken detail
// (settings/TakenRulesSettings) is only reachable via the SettingsDashboard's in-place state — there
// is no cross-page deep-link into a settings detail yet (follow-up).

// ── wire types (see api TakenRuntimeModels) ─────────────────────────────────────────────────────────
export interface TaakOption {
  id: string;
  label: string;
}

export interface TaakKpi {
  open: number;
  vandaag: number;
  achterstallig: number;
  afgerond: number;
  totaal: number;
}

export interface TaskDto {
  id: string;
  /** Animal id (Epic T4 follow-up) — lets standalone lists (nazorg Taken tab) link to the dossier. */
  dierId: number | null;
  title: string | null;
  description: string | null;
  status: string | null;
  statusLabel: string | null;
  priority: string | null;
  priorityLabel: string | null;
  priorityWeight: number;
  dueAt: string | null;
  overdue: boolean;
  dueToday: boolean;
  assignedTeam: string | null;
  teamLabel: string | null;
  /** Wat de taak doet als je hem uitvoert (ENTING, WEGING, ...); leeg bij een gewone taak. */
  handeling?: string | null;
  source: string | null;
  sourceLabel: string | null;
  automatic: boolean;
  templateId: string | null;
  ruleId: string | null;
  /** De wizard die bij deze taak hoort (van de taakregel), of null voor een gewone taak. */
  wizardDefinitionId?: string | null;
  wizardNaam?: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

/** De keuze "mijn team" in het teamfilter; geen echte teamcode. */
const MIJN_TEAM = "__mijn__";

export interface TakenPayload {
  dierId: number;
  vandaag: string;
  kpi: TaakKpi;
  taken: TaskDto[];
  teams: TaakOption[];
  prioriteiten: TaakOption[];
  statussen: TaakOption[];
  bronnen: TaakOption[];
  magBeheren: boolean;
  /** De teams van wie kijkt; leeg als hij in geen team zit. */
  mijnTeams?: string[];
}

export interface TakenListPayload {
  vandaag: string;
  taken: TaskDto[];
  page: number;
  size: number;
  teams: TaakOption[];
  prioriteiten: TaakOption[];
  statussen: TaakOption[];
  bronnen: TaakOption[];
  magBeheren: boolean;
}

// ── status/priority presentation (ids are stable; labels come from the server) ─────────────────────
const OPEN_STATUSSEN = ["OPEN", "IN_BEHANDELING"];
export const isOpenTaak = (taak: TaskDto): boolean => OPEN_STATUSSEN.includes(taak.status ?? "");

const STATUS_TONE: Record<string, StatusTone> = {
  OPEN: "neutral",
  IN_BEHANDELING: "warning",
  AFGEROND: "success",
  GEANNULEERD: "neutral",
  VERVALLEN: "neutral",
};
const PRIO_TONE: Record<string, StatusTone> = { HOOG: "danger", NORMAAL: "neutral", LAAG: "neutral" };

const TILE_PREVIEW_LIMIT = 5;

// ── shared form state ───────────────────────────────────────────────────────────────────────────────
interface TaakFormState {
  title: string;
  description: string;
  priority: string;
  dueAt: string;
  team: string;
  /** Een taak uit de takenlijst om te kiezen; leeg bij een vrije taak. */
  keuzeId?: number | null;
}

function formFromTaak(taak: TaskDto | null): TaakFormState {
  return {
    title: taak?.title ?? "",
    description: taak?.description ?? "",
    priority: taak?.priority ?? "NORMAAL",
    dueAt: taak?.dueAt ?? "",
    team: taak?.assignedTeam ?? "",
  };
}

// ── small building blocks ───────────────────────────────────────────────────────────────────────────
function KpiCard({ value, label, accent }: { value: number; label: string; accent: string }) {
  return (
    <div className={`fawkes-taken__kpi fawkes-taken__kpi--${accent}`}>
      <div className="fawkes-taken__kpi-value">{value}</div>
      <div className="fawkes-taken__kpi-label">{label}</div>
    </div>
  );
}

function DueLabel({ taak, vandaag, locale, t }: { taak: TaskDto; vandaag: string; locale: string; t: (k: string) => string }) {
  if (!taak.dueAt) return null;
  const text = taak.dueAt === vandaag ? t("taken.vandaag") : formatDate(taak.dueAt, locale);
  const accent = taak.overdue ? " fawkes-taken__due--overdue" : taak.dueToday ? " fawkes-taken__due--today" : "";
  return (
    <span className={`fawkes-taken__due${accent}`}>
      <CalendarIcon width={13} height={13} /> {text}
    </span>
  );
}

// The per-row status lifecycle actions (hybrid parity): OPEN → "In behandeling"; open → "Afronden"
// (+ Bewerken/Annuleren in the full view); closed (behalve VERVALLEN) → "Heropenen". Hidden entirely
// when the operator lacks the taken-beheer authority (magBeheren=false).
function TaakActies({
  taak,
  magBeheren,
  compact,
  onStatus,
  onEdit,
  onWizard,
  t,
}: {
  taak: TaskDto;
  magBeheren: boolean;
  compact: boolean;
  onStatus: (id: string, status: string) => void;
  onEdit?: (taak: TaskDto) => void;
  /** Alleen aanwezig als deze taak van een regel komt die een wizard start. */
  onWizard?: (taak: TaskDto) => void;
  t: (k: string) => string;
}) {
  const uitvoeren = useContext(UitvoerContext);
  if (!magBeheren) return null;
  const open = isOpenTaak(taak);
  const kanUitvoeren = open && !!uitvoeren && !!taak.handeling && taak.handeling !== "VRIJ" && taak.handeling !== "WIZARD";
  return (
    <div className="fawkes-taken__acties">
      {kanUitvoeren && (
        <button type="button" className="fawkes-taken__actie fawkes-taken__actie--groen" onClick={() => uitvoeren!(taak)}>
          <CheckIcon width={13} height={13} /> {t("taken.actie.uitvoeren").replace("%s", handelingLabel(taak.handeling))}
        </button>
      )}
      {/* Staat vooraan: dit is wat er met deze taak moet gebeuren, de rest is administratie eromheen. */}
      {open && taak.wizardDefinitionId && onWizard && (
        <button
          type="button"
          className="fawkes-taken__actie fawkes-taken__actie--groen"
          onClick={() => onWizard(taak)}
        >
          <SparklesIcon width={13} height={13} /> {t("taken.actie.wizardStarten")}
        </button>
      )}
      {open && taak.status === "OPEN" && (
        <button type="button" className="fawkes-taken__actie" onClick={() => onStatus(taak.id, "IN_BEHANDELING")}>
          {t("taken.actie.inBehandeling")}
        </button>
      )}
      {open && (
        <button
          type="button"
          className="fawkes-taken__actie fawkes-taken__actie--groen"
          onClick={() => onStatus(taak.id, "AFGEROND")}
        >
          <CheckIcon width={13} height={13} /> {t("taken.actie.afronden")}
        </button>
      )}
      {open && !compact && onEdit && (
        <button type="button" className="fawkes-taken__actie" onClick={() => onEdit(taak)}>
          <PencilIcon width={13} height={13} /> {t("taken.actie.bewerken")}
        </button>
      )}
      {open && !compact && (
        <button
          type="button"
          className="fawkes-taken__actie fawkes-taken__actie--grijs"
          onClick={() => onStatus(taak.id, "GEANNULEERD")}
        >
          {t("taken.actie.annuleren")}
        </button>
      )}
      {!open && !compact && taak.status !== "VERVALLEN" && (
        <button type="button" className="fawkes-taken__actie" onClick={() => onStatus(taak.id, "OPEN")}>
          {t("taken.actie.heropenen")}
        </button>
      )}
    </div>
  );
}

// The new/edit form (hybrid TaakForm): title (+ description in the full view), priority/deadline/team.
// Option lists (labels) come from the payload; the compact tile omits the description, like the hybrid.
function TaakForm({
  payload,
  taak,
  compact,
  saving,
  onSubmit,
  onCancel,
}: {
  payload: TakenPayload | TakenListPayload;
  taak: TaskDto | null;
  compact: boolean;
  saving: boolean;
  onSubmit: (form: TaakFormState) => void;
  onCancel: () => void;
}) {
  const t = useTranslate();
  const [form, setForm] = useState<TaakFormState>(() => formFromTaak(taak));
  const set = (patch: Partial<TaakFormState>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <form
      className="fawkes-taken__form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        onSubmit({ ...form, title: form.title.trim() });
      }}
    >
      <Field
        label={t("taken.form.titel")}
        value={form.title}
        required
        maxLength={250}
        onChange={(e) => set({ title: e.target.value })}
      />
      {!compact && (
        <TextareaField
          label={t("taken.form.omschrijving")}
          value={form.description}
          rows={3}
          onChange={(e) => set({ description: e.target.value })}
        />
      )}
      <div className="fawkes-taken__form-row">
        <SelectField
          label={t("taken.form.prioriteit")}
          value={form.priority}
          options={payload.prioriteiten.map((o) => ({ value: o.id, label: o.label }))}
          onChange={(e) => set({ priority: e.target.value })}
        />
        <Field label={t("taken.form.deadline")} type="date" value={form.dueAt} onChange={(e) => set({ dueAt: e.target.value })} />
        <SelectField
          label={t("taken.form.team")}
          value={form.team}
          options={[{ value: "", label: t("taken.form.geenTeam") }, ...payload.teams.map((o) => ({ value: o.id, label: o.label }))]}
          onChange={(e) => set({ team: e.target.value })}
        />
      </div>
      <div className="fawkes-taken__form-acties">
        <Button type="submit" variant="primary" disabled={saving || !form.title.trim()}>
          {taak ? t("common.save") : t("taken.form.toevoegen")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

/**
 * Een taak toevoegen, in een venster (Maarten, 2026-10-09). "Taak" is een keuzelijst waarin je ook kunt typen: kies
 * uit de takenlijst voor de soort van dit dier (Instellingen › Taken › Taken om te kiezen), of typ zelf een taak. Een
 * keuze vult prioriteit en team vooraf in, en de taak onthoudt wat hij bij uitvoeren opent.
 */
function NieuweTaakVenster({
  payload,
  saving,
  onSubmit,
  onCancel,
}: {
  payload: TakenPayload;
  saving: boolean;
  onSubmit: (form: TaakFormState) => void;
  onCancel: () => void;
}) {
  const t = useTranslate();
  const keuzesData = useCustom<TaakKeuze[]>({ url: `taak-keuzes?dierId=${payload.dierId}`, method: "get" });
  const keuzes = Array.isArray(keuzesData.data?.data) ? keuzesData.data!.data : [];
  const [tekst, setTekst] = useState("");
  const [gekozen, setGekozen] = useState<TaakKeuze | null>(null);
  const [lijstOpen, setLijstOpen] = useState(false);
  const [form, setForm] = useState<TaakFormState>(() => formFromTaak(null));
  const set = (patch: Partial<TaakFormState>) => setForm((f) => ({ ...f, ...patch }));
  const klaar = gekozen != null || tekst.trim() !== "";
  const [leegGemeld, setLeegGemeld] = useState(false);

  const kies = (k: TaakKeuze) => {
    setGekozen(k);
    setTekst(k.naam);
    setLijstOpen(false);
    set({ priority: k.standaardPrioriteit || "NORMAAL", team: k.standaardTeam ?? "", description: k.omschrijving ?? "" });
  };
  const typ = (waarde: string) => {
    setTekst(waarde);
    // Wie na een keuze verder typt, maakt er een eigen taak van.
    if (gekozen && waarde !== gekozen.naam) setGekozen(null);
    setLijstOpen(true);
  };

  const zoek = gekozen ? "" : tekst.trim().toLowerCase();
  const past = (k: TaakKeuze) => !zoek || k.naam.toLowerCase().includes(zoek);
  const voorSoort = keuzes.filter((k) => k.diersoortId != null && past(k));
  const voorElke = keuzes.filter((k) => k.diersoortId == null && past(k));
  const exact = keuzes.find((k) => k.naam.toLowerCase() === tekst.trim().toLowerCase());

  const verstuur = () => {
    // De knop blijft groen en klikbaar; zonder taak zegt het venster wat er nog moet.
    if (!klaar) { setLeegGemeld(true); return; }
    const keuze = gekozen ?? exact ?? null;
    onSubmit({ ...form, title: keuze ? "" : tekst.trim(), keuzeId: keuze?.id ?? null });
  };

  return (
    <Modal
      open
      title={t("taken.nieuw.titel")}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={verstuur}>
            {t("taken.form.toevoegen")}
          </Button>
        </>
      }
    >
      <div className="fawkes-taken__form fawkes-taken__form--venster taak-venster">
        <div className="taak-combo">
          <label htmlFor="taak-combo-invoer" className="fui-field__label">{t("taken.nieuw.taak")}</label>
          <div className="taak-combo__veld">
            <input
              id="taak-combo-invoer"
              className="fui-field__input"
              role="combobox"
              aria-expanded={lijstOpen}
              aria-controls="taak-combo-lijst"
              aria-autocomplete="list"
              autoComplete="off"
              maxLength={250}
              placeholder={t("taken.nieuw.kiesOfTyp")}
              value={tekst}
              aria-invalid={leegGemeld && !klaar ? true : undefined}
              onChange={(e) => { setLeegGemeld(false); typ(e.target.value); }}
              onFocus={() => setLijstOpen(true)}
              onBlur={() => window.setTimeout(() => setLijstOpen(false), 150)}
              onKeyDown={(e) => { if (e.key === "Escape") setLijstOpen(false); }}
            />
            <button type="button" className="taak-combo__pijl" tabIndex={-1} aria-label={t("taken.nieuw.toonLijst")}
              onMouseDown={(e) => { e.preventDefault(); setLijstOpen((o) => !o); }}>
              <ChevronDownIcon width={16} height={16} />
            </button>
          </div>
          {lijstOpen && (
            <ul id="taak-combo-lijst" className="taak-combo__lijst" role="listbox">
              {voorSoort.length > 0 && (
                <li className="taak-combo__groep" role="presentation">{voorSoort[0].diersoort ?? t("taken.nieuw.dezeSoort")}</li>
              )}
              {voorSoort.map((k) => (
                <li key={k.id} role="option" aria-selected={gekozen?.id === k.id}
                  onMouseDown={(e) => { e.preventDefault(); kies(k); }}>{k.naam}</li>
              ))}
              {voorElke.length > 0 && <li className="taak-combo__groep" role="presentation">{t("taken.nieuw.elkeSoort")}</li>}
              {voorElke.map((k) => (
                <li key={k.id} role="option" aria-selected={gekozen?.id === k.id}
                  onMouseDown={(e) => { e.preventDefault(); kies(k); }}>{k.naam}</li>
              ))}
              {tekst.trim() !== "" && !gekozen && !exact && (
                <li role="option" aria-selected={false} className="taak-combo__eigen"
                  onMouseDown={(e) => { e.preventDefault(); setLijstOpen(false); }}>
                  + {t("taken.nieuw.eigen").replace("%s", tekst.trim())}
                </li>
              )}
              {voorSoort.length + voorElke.length === 0 && tekst.trim() === "" && (
                <li className="taak-combo__leeg" role="presentation">{t("taken.nieuw.leeg")}</li>
              )}
            </ul>
          )}
        </div>
        {leegGemeld && !klaar && (
          <p className="taak-venster__fout" role="alert">{t("taken.nieuw.kiesEerst")}</p>
        )}
        {gekozen && gekozen.handeling !== "VRIJ" && (
          <p className="fawkes-taken__keuze-uitleg">
            {t("taken.nieuw.handeling").replace("%s", handelingLabel(gekozen.handeling).toLowerCase())}
          </p>
        )}
        <TextareaField label={t("taken.form.omschrijving")} value={form.description} rows={4}
          placeholder={t("taken.nieuw.omschrijvingPlaats")}
          onChange={(e) => set({ description: e.target.value })} />
        <div className="fawkes-taken__form-row">
          <SelectField
            label={t("taken.form.prioriteit")}
            value={form.priority}
            options={payload.prioriteiten.map((o) => ({ value: o.id, label: o.label }))}
            onChange={(e) => set({ priority: e.target.value })}
          />
          <Field label={t("taken.form.deadline")} type="date" value={form.dueAt} onChange={(e) => set({ dueAt: e.target.value })} />
          <SelectField
            label={t("taken.form.team")}
            value={form.team}
            options={[{ value: "", label: t("taken.form.geenTeam") }, ...payload.teams.map((o) => ({ value: o.id, label: o.label }))]}
            onChange={(e) => set({ team: e.target.value })}
          />
        </div>
      </div>
    </Modal>
  );
}

// One compact tile row (hybrid TaakRij): title + priority/auto badges, due/team meta, quick actions.
function TaakRij({
  taak,
  vandaag,
  magBeheren,
  locale,
  onStatus,
  onWizard,
  onEdit,
  onVerwijder,
  t,
}: {
  taak: TaskDto;
  vandaag: string;
  magBeheren: boolean;
  locale: string;
  onStatus: (id: string, status: string) => void;
  onWizard?: (taak: TaskDto) => void;
  /** Wijzigen en verwijderen vanaf de rij zelf (Maarten, 2026-10-11): "als het toch niet nodig is". */
  onEdit?: (taak: TaskDto) => void;
  onVerwijder?: (taak: TaskDto) => void;
  t: (k: string) => string;
}) {
  return (
    <li className={`fawkes-taken__rij${taak.overdue ? " fawkes-taken__rij--overdue" : ""}`}>
      <div className="fawkes-taken__rij-kop">
        <span className="fawkes-taken__titel">{taak.title}</span>
        <span className="fawkes-taken__badges">
          <StatusBadge tone={PRIO_TONE[taak.priority ?? ""] ?? "neutral"}>{taak.priorityLabel}</StatusBadge>
          {taak.automatic && <StatusBadge tone="neutral">{taak.sourceLabel}</StatusBadge>}
          {magBeheren && isOpenTaak(taak) && onEdit && (
            <button type="button" className="fawkes-taken__icoonknop" onClick={() => onEdit(taak)}
              aria-label={`${t("taken.actie.bewerken")}: ${taak.title}`} title={t("taken.actie.bewerken")}>
              <PencilIcon width={14} height={14} />
            </button>
          )}
          {magBeheren && isOpenTaak(taak) && onVerwijder && (
            <button type="button" className="fawkes-taken__icoonknop fawkes-taken__icoonknop--weg" onClick={() => onVerwijder(taak)}
              aria-label={`${t("taken.actie.verwijderen")}: ${taak.title}`} title={t("taken.actie.verwijderen")}>
              <TrashIcon width={14} height={14} />
            </button>
          )}
        </span>
      </div>
      <div className="fawkes-taken__meta">
        <DueLabel taak={taak} vandaag={vandaag} locale={locale} t={t} />
        {taak.teamLabel && (
          <span className="fawkes-taken__team">
            <UserIcon width={13} height={13} /> {taak.teamLabel}
          </span>
        )}
      </div>
      {isOpenTaak(taak) && (
        <TaakActies taak={taak} magBeheren={magBeheren} compact onStatus={onStatus} onWizard={onWizard} t={t} />
      )}
    </li>
  );
}

// One full-view card (hybrid TaakKaart): all badges + description + provenance + lifecycle actions;
// "Bewerken" swaps the card for the edit form in place.
function TaakKaart({
  taak,
  payload,
  locale,
  saving,
  onStatus,
  onUpdate,
  onWizard,
  t,
}: {
  taak: TaskDto;
  payload: TakenPayload;
  locale: string;
  saving: boolean;
  onStatus: (id: string, status: string) => void;
  onUpdate: (id: string, form: TaakFormState, onOk: () => void) => void;
  onWizard?: (taak: TaskDto) => void;
  t: (k: string) => string;
}) {
  const [edit, setEdit] = useState(false);

  if (edit) {
    return (
      <li className="fawkes-taken__kaart">
        <TaakForm
          payload={payload}
          taak={taak}
          compact={false}
          saving={saving}
          onSubmit={(form) => onUpdate(taak.id, form, () => setEdit(false))}
          onCancel={() => setEdit(false)}
        />
      </li>
    );
  }

  return (
    <li className={`fawkes-taken__kaart${taak.overdue ? " fawkes-taken__kaart--overdue" : ""}`}>
      <div className="fawkes-taken__rij-kop">
        <span className="fawkes-taken__titel">{taak.title}</span>
        <span className="fawkes-taken__badges">
          <StatusBadge tone={PRIO_TONE[taak.priority ?? ""] ?? "neutral"}>{taak.priorityLabel}</StatusBadge>
          <StatusBadge tone={STATUS_TONE[taak.status ?? ""] ?? "neutral"}>{taak.statusLabel}</StatusBadge>
          <StatusBadge tone="neutral">{taak.sourceLabel}</StatusBadge>
        </span>
      </div>
      {taak.description && <p className="fawkes-taken__omschrijving">{taak.description}</p>}
      <div className="fawkes-taken__meta">
        <DueLabel taak={taak} vandaag={payload.vandaag} locale={locale} t={t} />
        {taak.teamLabel && (
          <span className="fawkes-taken__team">
            <UserIcon width={13} height={13} /> {taak.teamLabel}
          </span>
        )}
        {taak.completedAt && <span className="fawkes-taken__afgerond-op">{formatDate(taak.completedAt, locale)}</span>}
      </div>
      {taak.automatic && (
        <div className="fawkes-taken__waarom">
          {t("taken.automatisch")}
          {taak.templateId ? ` · ${t("taken.automatisch.sjabloon").replace("%s", taak.templateId.slice(0, 8))}` : ""}
          {taak.ruleId ? ` · ${t("taken.automatisch.regel").replace("%s", taak.ruleId.slice(0, 8))}` : ""}
        </div>
      )}
      <TaakActies
        taak={taak}
        magBeheren={payload.magBeheren}
        compact={false}
        onStatus={onStatus}
        onEdit={() => setEdit(true)}
        onWizard={onWizard}
        t={t}
      />
    </li>
  );
}

// ── the full view (hybrid `view=tab`) ───────────────────────────────────────────────────────────────
// Exported for tests. All filtering is client-side over the payload the tile already holds (hybrid
// parity: search over title+description, a source select, and a "toon afgerond/geannuleerd" toggle
// that otherwise hides closed tasks).
export function TakenTabView({
  payload,
  saving,
  onStatus,
  onCreate,
  onUpdate,
  onWizard,
}: {
  payload: TakenPayload;
  saving: boolean;
  onStatus: (id: string, status: string) => void;
  onCreate: (form: TaakFormState, onOk: () => void) => void;
  onUpdate: (id: string, form: TaakFormState, onOk: () => void) => void;
  onWizard?: (taak: TaskDto) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [zoek, setZoek] = useState("");
  const [bron, setBron] = useState("");
  const [toonAfgerond, setToonAfgerond] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  // Welk team: alle, "mijn team" (de teams waar je zelf in zit), of één team.
  const [team, setTeam] = useState("");
  const mijnTeams = Array.isArray(payload.mijnTeams) ? payload.mijnTeams : [];

  const zichtbaar = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return payload.taken.filter((taak) => {
      if (!toonAfgerond && !isOpenTaak(taak)) return false;
      if (bron && taak.source !== bron) return false;
      if (team === MIJN_TEAM && !(taak.assignedTeam && mijnTeams.includes(taak.assignedTeam))) return false;
      if (team && team !== MIJN_TEAM && taak.assignedTeam !== team) return false;
      if (q && !`${taak.title ?? ""} ${taak.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [payload.taken, zoek, bron, toonAfgerond, team, mijnTeams]);

  return (
    <div className="fawkes-taken__tab">
      <p className="fawkes-taken__tab-intro">{t("taken.tab.subtitle")}</p>

      <div className="fawkes-taken__kpis fawkes-taken__kpis--groot">
        <KpiCard value={payload.kpi.open} label={t("taken.kpi.open")} accent="open" />
        <KpiCard value={payload.kpi.vandaag} label={t("taken.kpi.vandaag")} accent="vandaag" />
        <KpiCard value={payload.kpi.achterstallig} label={t("taken.kpi.achterstallig")} accent="achterstallig" />
        <KpiCard value={payload.kpi.afgerond} label={t("taken.kpi.afgerond")} accent="afgerond" />
        <KpiCard value={payload.kpi.totaal} label={t("taken.kpi.totaal")} accent="totaal" />
      </div>

      <div className="fawkes-taken__filterbalk">
        <div className="fawkes-products-search fawkes-taken__zoek">
          <SearchIcon className="fawkes-products-search__icon" width={16} height={16} />
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("taken.filter.zoek")}
            aria-label={t("taken.filter.zoek")}
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
          />
        </div>
        <SelectField
          label=""
          aria-label={t("taken.filter.bron")}
          value={bron}
          options={[{ value: "", label: t("taken.filter.alleBronnen") }, ...payload.bronnen.map((o) => ({ value: o.id, label: o.label }))]}
          onChange={(e) => setBron(e.target.value)}
        />
        <SelectField
          label=""
          aria-label={t("taken.filter.team")}
          value={team}
          options={[
            { value: "", label: t("taken.filter.alleTeams") },
            ...(mijnTeams.length > 0 ? [{ value: MIJN_TEAM, label: t("taken.filter.mijnTeam") }] : []),
            ...payload.teams.map((o) => ({ value: o.id, label: o.label })),
          ]}
          onChange={(e) => setTeam(e.target.value)}
        />
        <CheckboxField
          label={t("taken.filter.toonAfgerond")}
          checked={toonAfgerond}
          onChange={(e) => setToonAfgerond(e.target.checked)}
        />
        {payload.magBeheren && (
          <Button variant="primary" onClick={() => setFormOpen((open) => !open)}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("taken.nieuw")}
          </Button>
        )}
      </div>

      {formOpen && (
        <NieuweTaakVenster
          payload={payload}
          saving={saving}
          onSubmit={(form) => onCreate(form, () => setFormOpen(false))}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {zichtbaar.length === 0 ? (
        <p className="fawkes-tile-placeholder">{t("taken.leeg")}</p>
      ) : (
        <ul className="fawkes-taken__lijst">
          {zichtbaar.map((taak) => (
            <TaakKaart
              key={taak.id}
              taak={taak}
              payload={payload}
              locale={locale}
              saving={saving}
              onStatus={onStatus}
              onUpdate={onUpdate}
              onWizard={onWizard}
              t={t}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── the animal-detail tile (hybrid `view=tegel`) ────────────────────────────────────────────────────
export function TakenTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom({ url: `animals/${animalId}/taken`, method: "get" });
  const payload = data?.data as TakenPayload | undefined;
  // The api has no server-side "current vestiging" session for task creation (unlike Jmix), so the
  // SPA stamps its active vestiging on POST /api/taken explicitly (null when unresolved).
  const { data: vestigingData } = useCustom({ url: "vestigingen/current", method: "get" });
  const currentVestigingId = (vestigingData?.data as { id?: number } | undefined)?.id ?? null;

  const { mutate, isLoading: saving } = useCustomMutation();
  // The i18n key of the last failed save, or null.
  const [fout, setFout] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [tabOpen, setTabOpen] = useState(false);
  // De taak waarvan de wizard openstaat. Eén tegelijk: een wizard is een gesprek met het dier voor je,
  // geen tabblad dat je ernaast laat staan.
  const [wizardVoor, setWizardVoor] = useState<TaskDto | null>(null);

  const run = (
    url: string,
    method: "post" | "patch",
    values: Record<string, unknown>,
    onOk?: () => void,
    conflictKey?: string,
  ) => {
    setFout(null);
    mutate(
      { url, method, values, successNotification: false, errorNotification: false },
      {
        onSuccess: () => {
          refetch();
          onOk?.();
        },
        onError: (err) =>
          setFout(conflictKey && (err as { statusCode?: number }).statusCode === 409 ? conflictKey : "taken.fout"),
      },
    );
  };

  // A 409 here: the task is a cancelled/expired automatic task whose automation key a newer live task
  // now holds, so it can't become open/in behandeling/afgerond again. The api's reason is Dutch-only and
  // not in the error body, hence our own message.
  const changeStatus = (id: string, status: string) =>
    run(`taken/${id}/status`, "post", { status }, undefined, "taken.fout.conflict");

  /**
   * De wizard is weg. Was hij afgerond (geen concept), dan is de taak gedaan — dat is precies wat de
   * regel bedoelde toen hij deze taak aanmaakte, en die twee keer laten afvinken is werk voor niets.
   * Een concept laat de taak openstaan.
   */
  const wizardKlaar = (taak: TaskDto, concept: boolean) => {
    setWizardVoor(null);
    if (!concept && isOpenTaak(taak)) {
      changeStatus(taak.id, "AFGEROND");
    } else {
      refetch();
    }
  };
  const createTaak = (form: TaakFormState, onOk: () => void) =>
    run(
      "taken",
      "post",
      {
        dierId: Number(animalId),
        vestigingId: currentVestigingId,
        title: form.title.trim() || null,
        description: form.description.trim() || null,
        priority: form.priority,
        dueAt: form.dueAt || null,
        team: form.team || null,
        keuzeId: form.keuzeId ?? null,
      },
      onOk,
    );
  const [bewerkTaak, setBewerkTaak] = useState<TaskDto | null>(null);
  const [verwijderTaak, setVerwijderTaak] = useState<TaskDto | null>(null);
  const updateTaak = (id: string, form: TaakFormState, onOk: () => void) =>
    run(
      `taken/${id}`,
      "patch",
      {
        title: form.title,
        description: form.description.trim() || null,
        priority: form.priority,
        dueAt: form.dueAt || null,
        team: form.team || null,
      },
      onOk,
    );

  // Uitvoeren: de tegel die over deze handeling gaat opent zijn invoerscherm. Meldt hij terug dat het opgeslagen
  // is, dan is de taak af. Pakt geen tegel hem op (die tegel staat uit), dan zeggen we dat.
  const uitvoeren = (taak: TaskDto) => {
    setFout(null);
    setTabOpen(false);
    const opgepakt = vraagUitvoering({
      taakId: taak.id, dierId: Number(animalId), handeling: taak.handeling as Handeling, titel: taak.title ?? "",
    });
    if (!opgepakt) setFout("taken.uitvoeren.geenTegel");
  };
  useTaakUitgevoerd((taakId) => {
    const taak = payload?.taken.find((x) => x.id === taakId);
    if (taak && isOpenTaak(taak)) changeStatus(taakId, "AFGEROND");
  });

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError || !payload) return <p className="fawkes-error">{t("animalDetailView.tileLoadFailed")}</p>;

  const open = payload.taken.filter(isOpenTaak);
  const top = open.slice(0, TILE_PREVIEW_LIMIT);

  return (
    <UitvoerContext.Provider value={uitvoeren}>
    <div className="fawkes-taken">
      <div className="fawkes-taken__kpis">
        <KpiCard value={payload.kpi.open} label={t("taken.kpi.open")} accent="open" />
        <KpiCard value={payload.kpi.vandaag} label={t("taken.kpi.vandaag")} accent="vandaag" />
        <KpiCard value={payload.kpi.achterstallig} label={t("taken.kpi.achterstallig")} accent="achterstallig" />
      </div>

      {fout && <p className="fawkes-error">{t(fout)}</p>}

      {top.length === 0 ? (
        <p className="fawkes-tile-placeholder">{t("taken.tile.empty")}</p>
      ) : (
        <ul className="fawkes-taken__lijst fawkes-taken__lijst--compact">
          {top.map((taak) => (
            <TaakRij
              key={taak.id}
              taak={taak}
              vandaag={payload.vandaag}
              magBeheren={payload.magBeheren}
              locale={locale}
              onStatus={changeStatus}
              onWizard={setWizardVoor}
              onEdit={setBewerkTaak}
              onVerwijder={setVerwijderTaak}
              t={t}
            />
          ))}
        </ul>
      )}
      {bewerkTaak && (
        <Modal open title={t("taken.bewerk.titel")} onClose={() => setBewerkTaak(null)}>
          <div className="taak-venster">
            <TaakForm
              payload={payload}
              taak={bewerkTaak}
              compact={false}
              saving={saving}
              onSubmit={(form) => updateTaak(bewerkTaak.id, form, () => setBewerkTaak(null))}
              onCancel={() => setBewerkTaak(null)}
            />
          </div>
        </Modal>
      )}
      {verwijderTaak && (
        <ConfirmDialog
          title={t("taken.verwijder.titel")}
          confirmLabel={t("taken.actie.verwijderen")}
          cancelLabel={t("common.cancel")}
          danger
          onCancel={() => setVerwijderTaak(null)}
          onConfirm={() => { changeStatus(verwijderTaak.id, "GEANNULEERD"); setVerwijderTaak(null); }}
        >
          <p>{t("taken.verwijder.uitleg").replace("%s", verwijderTaak.title ?? "")}</p>
        </ConfirmDialog>
      )}
      {open.length > top.length && (
        <p className="fawkes-taken__meer">{t("taken.tile.meer").replace("%d", String(open.length - top.length))}</p>
      )}

      {formOpen && payload.magBeheren && (
        <NieuweTaakVenster
          payload={payload}
          saving={saving}
          onSubmit={(form) => createTaak(form, () => setFormOpen(false))}
          onCancel={() => setFormOpen(false)}
        />
      )}

      <div className="fawkes-taken__voet">
        {payload.magBeheren ? (
          <Button variant="primary" onClick={() => setFormOpen((o) => !o)}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("taken.nieuw")}
          </Button>
        ) : (
          <span />
        )}
        <button type="button" className="fawkes-taken__alles" onClick={() => setTabOpen(true)}>
          {t("taken.tile.alles")}
        </button>
      </div>

      {wizardVoor?.wizardDefinitionId && (
        <WizardRunModal
          animalId={animalId}
          definitionId={wizardVoor.wizardDefinitionId}
          onKlaar={(concept) => wizardKlaar(wizardVoor, concept)}
          onClose={() => setWizardVoor(null)}
        />
      )}

      <Modal open={tabOpen} title={t("taken.tab.title")} onClose={() => setTabOpen(false)}>
        {/* Tile-scoped wide wrapper — the shared Modal caps at 460px, but the full taken view is a
            page-like overview (see .fui-modal:has(.fawkes-taken__tab) in styles.css). */}
        {tabOpen && (
          <>
            {fout && <p className="fawkes-error">{t(fout)}</p>}
            <TakenTabView
              payload={payload}
              saving={saving}
              onStatus={changeStatus}
              onCreate={createTaak}
              onUpdate={updateTaak}
              onWizard={(taak) => { setTabOpen(false); setWizardVoor(taak); }}
            />
          </>
        )}
      </Modal>
    </div>
    </UitvoerContext.Provider>
  );
}
